import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, Gauge, Clock, Heart, MapPin } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { formatEUR, formatKm, countryFlag, statusStyle } from "../lib/format";
import OpportunityBadge from "./OpportunityBadge";
import TrattativaModal from "./TrattativaModal";
import { useFavorites } from "../lib/favorites";

/**
 * Dealer-first vehicle card:
 *  – photo + brand/model/trim + one-liner year · km · fuel
 *  – HUMAN one-line pricing: "Compri a X · Rivendi ~Y · Guadagno stimato ~Z"
 *  – ONE primary CTA "La voglio" → opens TrattativaModal directly
 *  – Advanced metrics (AI Score badge, ROI, resale days, opportunity index)
 *    live inside a "Mostra dettagli" expander per card so the primary view
 *    stays uncluttered.
 * Nothing removed — all data still rendered, just reorganized.
 */
export default function VehicleCard({ vehicle, className = "" }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { favorites, toggle } = useFavorites();
  const [showDetails, setShowDetails] = useState(false);
  const [trattativaOpen, setTrattativaOpen] = useState(false);
  const a = vehicle.analysis || {};
  const image = vehicle.images?.[0];
  const margin = a.estimated_margin_eur || 0;
  const destCountry = a.destination_country || (vehicle.country === "DE" ? "IT" : "DE");
  const destValue =
    a.destination_market_value ??
    (destCountry === "IT" ? vehicle.market_value_it : vehicle.market_value_de) ??
    (vehicle.price_eur + margin);
  const gainColor = margin >= 0 ? "text-emerald-600" : "text-red-600";
  const isFav = favorites.has(vehicle.id);

  const goVehicle = () => navigate(`/vehicle/${vehicle.id}`);

  const goDealer = (e) => {
    if (!vehicle.dealer?.id) return;
    e.preventDefault();
    e.stopPropagation();
    navigate(`/dealers/${vehicle.dealer.id}`);
  };

  const toggleFav = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggle(vehicle.id);
  };

  const openLaVoglio = (e) => {
    e.stopPropagation();
    setTrattativaOpen(true);
  };

  const toggleDetails = (e) => {
    e.stopPropagation();
    setShowDetails((v) => !v);
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        whileHover={{ y: -3 }}
        className={className}
      >
        <div
          data-testid={`vehicle-card-${vehicle.id}`}
          className="rounded-2xl bg-white border border-slate-100 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200"
        >
          {/* Media */}
          <div
            className="relative aspect-[16/10] bg-slate-100 overflow-hidden cursor-pointer"
            onClick={goVehicle}
            role="link"
            tabIndex={0}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && goVehicle()}
          >
            {image && (
              <img
                src={image}
                alt={`${vehicle.brand} ${vehicle.model}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            )}
            <div className="absolute inset-x-0 top-0 flex items-start justify-end p-3">
              <span
                className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-md ${statusStyle(
                  vehicle.status
                )}`}
                data-testid={`vehicle-status-${vehicle.id}`}
              >
                {t(`status.${vehicle.status}`)}
              </span>
            </div>
            <div className="absolute bottom-3 left-3 inline-flex items-center gap-1 text-xs font-semibold bg-white/90 backdrop-blur px-2 py-1 rounded-md text-slate-700">
              <span aria-hidden>{countryFlag(vehicle.country)}</span>
              <span>{vehicle.country}</span>
            </div>
            <button
              type="button"
              onClick={toggleFav}
              data-testid={`vehicle-fav-${vehicle.id}`}
              aria-label={isFav ? t("vehicle.unsave") : t("vehicle.save")}
              aria-pressed={isFav}
              className={`absolute bottom-3 right-3 w-9 h-9 rounded-full flex items-center justify-center backdrop-blur transition-all ${
                isFav
                  ? "bg-rose-500 text-white shadow-lg shadow-rose-500/30"
                  : "bg-white/90 text-slate-500 hover:text-rose-500"
              }`}
            >
              <Heart className={`w-4 h-4 ${isFav ? "fill-current" : ""}`} />
            </button>
          </div>

          {/* Body */}
          <div className="p-4">
            <div
              className="cursor-pointer"
              onClick={goVehicle}
              role="link"
              tabIndex={0}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && goVehicle()}
            >
              <h3 className="font-heading font-semibold text-[15px] tracking-tight text-slate-950 leading-snug truncate">
                {vehicle.brand} {vehicle.model}
              </h3>
              <p className="text-xs text-slate-500 truncate mt-0.5">{vehicle.trim}</p>

              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                <span className="tabular-nums">{vehicle.year}</span>
                <span className="text-slate-300">·</span>
                <span className="tabular-nums">{formatKm(vehicle.km)}</span>
                <span className="text-slate-300">·</span>
                <span className="capitalize">{t(`fuel.${vehicle.fuel}`, vehicle.fuel)}</span>
              </div>
            </div>

            {/* Human one-liner pricing (star of the card) */}
            <div
              data-testid={`vehicle-card-pricing-${vehicle.id}`}
              className="mt-3 rounded-xl bg-slate-50 border border-slate-100 p-3 space-y-1.5"
            >
              <PricingRow
                label={t("card.buy_at")}
                value={formatEUR(vehicle.price_eur)}
                testid={`vehicle-card-buy-${vehicle.id}`}
              />
              <PricingRow
                label={t("card.resell_at")}
                value={`~${formatEUR(destValue)}`}
                testid={`vehicle-card-resell-${vehicle.id}`}
              />
              <div className="pt-1.5 mt-1 border-t border-slate-200/70 flex items-baseline justify-between gap-3">
                <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 inline-flex items-center gap-1">
                  {t("card.gain_estimated")}
                  <span
                    data-testid={`vehicle-card-demo-tag-${vehicle.id}`}
                    className="normal-case tracking-normal text-[9px] font-medium text-slate-400 italic"
                  >
                    · {t(vehicle.is_demo ? "dataTag.demo_short" : "dataTag.estimate_short")}
                  </span>
                </span>
                <span
                  data-testid={`vehicle-card-gain-${vehicle.id}`}
                  className={`font-heading font-bold text-lg tabular-nums ${gainColor}`}
                >
                  {margin >= 0 ? "~+" : "~"}
                  {formatEUR(margin)}
                </span>
              </div>
            </div>

            {/* Primary CTA */}
            <button
              type="button"
              onClick={openLaVoglio}
              data-testid={`vehicle-card-la-voglio-${vehicle.id}`}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold h-11 transition-colors"
            >
              {t("card.la_voglio")}
            </button>

            {/* Details expander (advanced metrics live here) */}
            <button
              type="button"
              onClick={toggleDetails}
              data-testid={`vehicle-card-details-toggle-${vehicle.id}`}
              aria-expanded={showDetails}
              className="mt-3 w-full inline-flex items-center justify-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-900 transition-colors"
            >
              {showDetails ? t("card.hide_details") : t("card.show_details")}
              {showDetails ? (
                <ChevronUp className="w-3 h-3" />
              ) : (
                <ChevronDown className="w-3 h-3" />
              )}
            </button>

            <AnimatePresence initial={false}>
              {showDetails && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  data-testid={`vehicle-card-details-${vehicle.id}`}
                  className="overflow-hidden"
                >
                  <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-500">{t("card.opportunity_label")}</span>
                      <OpportunityBadge
                        index={a.opportunity_index}
                        score={a.ai_score}
                        size="sm"
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-500 inline-flex items-center gap-1">
                        <Gauge className="w-3 h-3" /> {t("vehicle.roi")}
                      </span>
                      <span className="tabular-nums font-semibold text-slate-800">
                        {a.roi_percent != null ? `${a.roi_percent.toFixed(1)}%` : "—"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-500 inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {t("vehicle.avg_resale_days")}
                      </span>
                      <span className="tabular-nums font-semibold text-slate-800">
                        {a.avg_resale_days != null ? `${a.avg_resale_days} g` : "—"}
                      </span>
                    </div>
                    <div className="pt-2 mt-2 border-t border-slate-100 text-xs text-slate-500">
                      {vehicle.dealer?.id ? (
                        <button
                          type="button"
                          onClick={goDealer}
                          data-testid={`vehicle-card-dealer-link-${vehicle.id}`}
                          className="inline-flex items-center gap-1 hover:text-blue-600 transition-colors"
                        >
                          <MapPin className="w-3 h-3" />
                          <span className="truncate">{vehicle.dealer.name}</span>
                        </button>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {vehicle.dealer?.name || "—"}
                        </span>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>

      <TrattativaModal
        open={trattativaOpen}
        onOpenChange={setTrattativaOpen}
        vehicle={vehicle}
        defaultType="acquisto"
      />
    </>
  );
}

function PricingRow({ label, value, testid }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
        {label}
      </span>
      <span data-testid={testid} className="tabular-nums font-semibold text-slate-900 text-sm">
        {value}
      </span>
    </div>
  );
}
